import React, { FormEvent, useState } from 'react'
import { GridItem, GridContainer } from '@entur/grid'
import { space } from '@entur/tokens'
import { Heading3 } from '@entur/typography'
import { PrimaryButton, SecondaryButton } from '@entur/button'
import { useNavigate, useParams } from 'react-router'
import { SmallAlertBox } from '@entur/alert'

import { useApi } from '../../api-clients/client-hooks'
import { patchEmployee } from '../../api-clients/eba-clients'
import { fieldsToObject, formIsValid, validateAllFields, ValidationFormField } from '../../utils/form-utils'
import { Employee, FetchState } from '../../api-clients/api-types'
import { ModalErrorAlert } from '../modal/modal-error-alert'
import { useEmployee } from '../../contexts/employee-context'
import { ssnErrorMessage } from '../../utils/ssn-utils'
import {
  dateOfBirthFormField,
  addressFormField,
  firstNameFormField,
  postalCodeFormField,
  surnameFormField,
} from '../../utils/common-form-fields'
import { ssnField, SsnFormField } from '../form/ssn-form-field'
import { ValidatableTextField } from '../form/validatable-text-field'
import ValidatableDatePicker from '../form/validatable-date-picker'

type State = {
  firstName: ValidationFormField<string>
  surname: ValidationFormField<string>
  dateOfBirth: ValidationFormField<Date | undefined>
  ssn: ValidationFormField<string | undefined>
  address: ValidationFormField<string>
  postalCode: ValidationFormField<string>
}

function initialState(employee: Employee): State {
  return {
    firstName: firstNameFormField(employee.firstName, true),
    surname: surnameFormField(employee.surname, true),
    dateOfBirth: dateOfBirthFormField(employee.dateOfBirth),
    ssn: ssnField(employee.ssn.toString()),
    address: addressFormField(employee.addressLine, true),
    postalCode: postalCodeFormField(employee.postalPlace.code, true),
  }
}

type ChangeEmployeeFormProps = {
  onSubmit?: () => void
  onAbort?: () => void
}
export const ChangeEmployeeForm: React.FC<ChangeEmployeeFormProps> = (props) => {
  const navigate = useNavigate()
  const navParams = useParams()
  const { employee } = useEmployee()

  const [isValid, setValid] = useState(true)
  const [ssnError, setSsnError] = useState<string>()
  const [state, setState] = useState(initialState(employee))

  const [response, doPatch] = useApi(patchEmployee, {
    successCallback: (data) => {
      navigate(`/ansatt/${data.id}#endret`, { state: navParams.state })
    },
    errorCallback: (error) => {
      setSsnError(ssnErrorMessage(error))
    },
  })

  const updateEmployee = (e: FormEvent) => {
    e.preventDefault()
    const localState = validateAllFields(state)
    const valid = formIsValid(localState)
    if (valid) {
      setValid(true)
      doPatch({
        id: employee.id,
        body: fieldsToObject(state || {}),
      })
    } else {
      setValid(false)
      setState(localState)
    }
    props.onSubmit?.()
  }

  return (
    <form onSubmit={updateEmployee}>
      <GridContainer>
        <GridItem small={12} medium={12} large={10}>
          <Heading3>Personalia</Heading3>
          <GridContainer spacing="medium">
            <GridItem small={3}>
              <ValidatableTextField
                field={state.firstName}
                update={(e) =>
                  setState({
                    ...state,
                    firstName: e,
                  })
                }
              />
            </GridItem>

            <GridItem small={3}>
              <ValidatableTextField
                field={state.surname}
                update={(e) =>
                  setState({
                    ...state,
                    surname: e,
                  })
                }
              />
            </GridItem>

            <GridItem small={3}>
              <ValidatableDatePicker
                date={state.dateOfBirth}
                update={(e) => setState({ ...state, dateOfBirth: e })}
              />
            </GridItem>
            <GridItem small={3}>
              <SsnFormField
                dateOfBirthField={state.dateOfBirth}
                ssnField={state.ssn}
                updateSsn={(e) =>
                  setState({
                    ...state,
                    ssn: e,
                  })
                }
              />
            </GridItem>
          </GridContainer>
          <Heading3>Adresse</Heading3>
          <GridContainer spacing="medium">
            <GridItem small={3}>
              <ValidatableTextField
                field={state.address}
                update={(e) =>
                  setState({
                    ...state,
                    address: e,
                  })
                }
              />
            </GridItem>

            <GridItem small={3}>
              <ValidatableTextField
                field={state.postalCode}
                update={(e) =>
                  setState({
                    ...state,
                    postalCode: e,
                  })
                }
              />
            </GridItem>
          </GridContainer>
        </GridItem>

        <GridItem small={6} style={{ marginTop: space.extraLarge5 }}>
          {response.status === FetchState.ERROR && !ssnError && (
            <ModalErrorAlert title="Får ikke endret rettighetseier" />
          )}
          {!isValid && (
            <SmallAlertBox variant="error" width="fit-content" style={{ marginBottom: space.large }}>
              Alle felt må være fylt ut med gyldige verdier for å sende inn skjemaet.
            </SmallAlertBox>
          )}
          {ssnError && (
            <SmallAlertBox variant="error" width="fit-content" style={{ marginBottom: space.large }}>
              {ssnError}
            </SmallAlertBox>
          )}
          <PrimaryButton
            style={{ marginRight: space.medium }}
            type="submit"
            loading={response.status === FetchState.LOADING}
            disabled={response.status === FetchState.LOADING}
          >
            Lagre endringer
          </PrimaryButton>
          <SecondaryButton
            type="button"
            onClick={() => {
              props.onAbort?.()
            }}
            disabled={response.status === FetchState.LOADING}
          >
            Forkast endringer
          </SecondaryButton>
        </GridItem>
      </GridContainer>
    </form>
  )
}
